package org.example.project.service

import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.mutableStateOf
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.MapSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.Json
import org.example.project.utils.All
import kotlin.collections.plus

object FrameworkUserSettingsService {
    private val observedSettingsFiles = listOf(
        "columnsettings.json",
        "ringsettings.json",
        "csvimportsettings.json",
        "csvexportsettings.json",
        "filterprofilesettings.json",
        "letterprofilesettings.json",
        "popupsettings.json",
        "dashboardfeeds.json"
    )

    private val json = Json {
        prettyPrint = true
        ignoreUnknownKeys = true
    }

    private var columnSettingsCache: List<All> = emptyList()
    private var ringSettingsCache by mutableStateOf<List<All>>(emptyList())
    private var ringSettingsLoaded: Boolean = false
    private var csvImportSettingsCache: List<UserCsvProfileSettings> = emptyList()
    private var csvExportSettingsCache: List<UserCsvProfileSettings> = emptyList()
    private var filterProfileSettingsCache: List<UserCsvProfileSettings> = emptyList()
    private var letterProfileSettingsCache: List<UserLetterProfileSettings> = emptyList()
    private var popupSettingsCache: Map<String, List<Double>> = emptyMap()

    private fun currentUserId(): String =
        LoginService.getUser()?.id.orEmpty()

    suspend fun ensureLoaded(
        art: String,
        userId: String,
        forceReload: Boolean = false,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        if (userId.isBlank()) return
        if (forceReload || columnSettingsCache.isEmpty()) loadColumnSettings(art, userId, loadUserSettingsFile)
        if (forceReload || !ringSettingsLoaded) loadRingSettings(art, userId, loadUserSettingsFile)
        if (forceReload || csvImportSettingsCache.isEmpty()) loadCsvSettings(art, userId, true, loadUserSettingsFile)
        if (forceReload || csvExportSettingsCache.isEmpty()) loadCsvSettings(art, userId, false, loadUserSettingsFile)
        if (forceReload || filterProfileSettingsCache.isEmpty()) loadFilterProfileSettings(art, userId, loadUserSettingsFile)
        if (forceReload || letterProfileSettingsCache.isEmpty()) loadLetterProfileSettings(art, userId, loadUserSettingsFile)
        if (forceReload || popupSettingsCache.isEmpty()) loadPopupSettings(art, userId, loadUserSettingsFile)
    }

    fun getPopupSettings(key: String): List<Double>? = popupSettingsCache[key]

    fun currentUserSettingsVersion(): Long =
        observedSettingsFiles.sumOf(UserSettingsApi::version)

    fun currentUserRingSettingsVersion(): Long = UserSettingsApi.version("ringsettings.json")

    suspend fun ensureCurrentUserRingSettingsLoaded(forceReload: Boolean = false) {
        val userId = currentUserId()
        if (userId.isBlank()) return
        if (forceReload || !ringSettingsLoaded) loadRingSettings("", userId) { _, id, file ->
            UserSettingsApi.loadUserSettingsFile(id, file)
        }
    }

    suspend fun saveCurrentUserPopupSettings(key: String, x: Double, y: Double, w: Double, h: Double) {
        val userId = currentUserId()
        if (userId.isBlank()) return
        popupSettingsCache = popupSettingsCache + (key to listOf(x, y, w, h))
        val body = json.encodeToString(
            MapSerializer(String.serializer(), ListSerializer(Double.serializer())),
            popupSettingsCache
        )
        UserSettingsApi.saveUserSettingsFile(userId, "popupsettings.json", body)
    }

    fun getUserColumnSettings(screenKey: String): All? =
        columnSettingsCache.firstOrNull { it.screenKey == screenKey }

    fun getUserRingSettings(): List<String> =
        ringSettingsCache.map { it.screenKey }

    fun getUserCsvImportSettings(screenKey: String): UserCsvProfileSettings? =
        csvImportSettingsCache.firstOrNull { it.screenKey == screenKey }

    fun getUserCsvExportSettings(screenKey: String): UserCsvProfileSettings? =
        csvExportSettingsCache.firstOrNull { it.screenKey == screenKey }

    fun getUserFilterProfileSettings(screenKey: String): UserCsvProfileSettings? =
        filterProfileSettingsCache.firstOrNull { it.screenKey == screenKey }

    fun getUserLetterProfileSettings(screenKey: String): UserLetterProfileSettings? =
        letterProfileSettingsCache.firstOrNull { it.screenKey == screenKey }

    suspend fun ensureCurrentUserSettingsLoaded(forceReload: Boolean = false) {
        val userId = currentUserId()
        if (userId.isBlank()) return
        ensureLoaded(
            art = "",
            userId = userId,
            forceReload = forceReload,
            loadUserSettingsFile = { _, resolvedUserId, fileName ->
                UserSettingsApi.loadUserSettingsFile(resolvedUserId, fileName)
            }
        )
    }

    suspend fun saveCurrentUserColumnSettings(
        screenKey: String,
        order: List<String>,
        hidden: Set<String>,
        limit: Int = 2000,
        settingsWerte: Map<String, String>? = null
    ) {
        val userId = currentUserId()
        if (screenKey.isBlank() || userId.isBlank()) return
        saveUserColumnSettings(
            art = "",
            userId = userId,
            screenKey = screenKey,
            order = order,
            hidden = hidden,
            limit = limit,
            settingsWerte = settingsWerte,
            saveUserSettingsFile = { _, resolvedUserId, fileName, body ->
                UserSettingsApi.saveUserSettingsFile(resolvedUserId, fileName, body)
            }
        )
    }

    suspend fun saveCurrentUserCsvImportSettings(
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?
    ) {
        val userId = currentUserId()
        if (screenKey.isBlank() || userId.isBlank()) return
        saveUserCsvImportSettings(
            art = "",
            userId = userId,
            screenKey = screenKey,
            profiles = profiles,
            selectedProfile = selectedProfile,
            saveUserSettingsFile = { _, resolvedUserId, fileName, body ->
                UserSettingsApi.saveUserSettingsFile(resolvedUserId, fileName, body)
            }
        )
    }

    suspend fun saveCurrentUserCsvExportSettings(
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?
    ) {
        val userId = currentUserId()
        if (screenKey.isBlank() || userId.isBlank()) return
        saveUserCsvExportSettings(
            art = "",
            userId = userId,
            screenKey = screenKey,
            profiles = profiles,
            selectedProfile = selectedProfile,
            saveUserSettingsFile = { _, resolvedUserId, fileName, body ->
                UserSettingsApi.saveUserSettingsFile(resolvedUserId, fileName, body)
            }
        )
    }

    suspend fun saveCurrentUserRingSettings(
        order: List<String>,
        limit: Int = 2000
    ) {
        val userId = currentUserId()
        if (userId.isBlank()) return
        val nextSlots = normalizeRingSlots(order.take(limit))
        val body = json.encodeToString(ListSerializer(All.AllNames.serializer()), nextSlots)
        if (UserSettingsApi.saveUserSmartRingSettings(userId, body)) {
            ringSettingsCache = nextSlots
            ringSettingsLoaded = true
            UserSettingsApi.markChanged("ringsettings.json")
            GlobalManager.markJsonChanged("ringsettings.json", "user/$userId/settings/ringsettings.json")
        }
    }

    suspend fun saveCurrentUserFilterProfileSettings(
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?
    ) {
        val userId = currentUserId()
        if (screenKey.isBlank() || userId.isBlank()) return
        saveUserFilterProfileSettings(
            art = "",
            userId = userId,
            screenKey = screenKey,
            profiles = profiles,
            selectedProfile = selectedProfile,
            saveUserSettingsFile = { _, resolvedUserId, fileName, body ->
                UserSettingsApi.saveUserSettingsFile(resolvedUserId, fileName, body)
            }
        )
    }

    suspend fun saveCurrentUserLetterProfileSettings(
        screenKey: String,
        profiles: Map<String, String>,
        selectedProfile: String?
    ) {
        val userId = currentUserId()
        if (screenKey.isBlank() || userId.isBlank()) return
        saveUserLetterProfileSettings(
            art = "",
            userId = userId,
            screenKey = screenKey,
            profiles = profiles,
            selectedProfile = selectedProfile,
            saveUserSettingsFile = { _, resolvedUserId, fileName, body ->
                UserSettingsApi.saveUserSettingsFile(resolvedUserId, fileName, body)
            }
        )
    }

    suspend fun saveUserColumnSettings(
        art: String,
        userId: String,
        screenKey: String,
        order: List<String>,
        hidden: Set<String>,
        limit: Int,
        settingsWerte: Map<String, String>?,
        saveUserSettingsFile: suspend (String, String, String, String) -> Unit
    ) {
        if (screenKey.isBlank() || userId.isBlank()) return
        val previous = columnSettingsCache.firstOrNull { it.screenKey == screenKey }
        val mergedSettingsWerte = if (settingsWerte == null) previous?.settingsWerte ?: emptyMap() else (previous?.settingsWerte ?: emptyMap()) + settingsWerte
        columnSettingsCache = columnSettingsCache.filter { it.screenKey != screenKey } +
            All(screenKey = screenKey, columns = order, hidden = hidden, visibleLimit = limit, settingsWerte = mergedSettingsWerte)
        val body = json.encodeToString(ListSerializer(All.AllNames.serializer()), columnSettingsCache)
        saveUserSettingsFile(art, userId, "columnsettings.json", body)
    }

    suspend fun saveUserCsvImportSettings(
        art: String,
        userId: String,
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?,
        saveUserSettingsFile: suspend (String, String, String, String) -> Unit
    ) {
        if (screenKey.isBlank() || userId.isBlank()) return
        csvImportSettingsCache = csvImportSettingsCache.filter { it.screenKey != screenKey } +
                UserCsvProfileSettings(
                    screenKey = screenKey,
                    profiles = profiles,
                    selectedProfile = selectedProfile.orEmpty()
                )
        val body = json.encodeToString(ListSerializer(UserCsvProfileSettings.serializer()), csvImportSettingsCache)
        saveUserSettingsFile(art, userId, "csvimportsettings.json", body)
    }

    suspend fun saveUserCsvExportSettings(
        art: String,
        userId: String,
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?,
        saveUserSettingsFile: suspend (String, String, String, String) -> Unit
    ) {
        if (screenKey.isBlank() || userId.isBlank()) return
        csvExportSettingsCache = csvExportSettingsCache.filter { it.screenKey != screenKey } +
                UserCsvProfileSettings(
                    screenKey = screenKey,
                    profiles = profiles,
                    selectedProfile = selectedProfile.orEmpty()
                )
        val body = json.encodeToString(ListSerializer(UserCsvProfileSettings.serializer()), csvExportSettingsCache)
        saveUserSettingsFile(art, userId, "csvexportsettings.json", body)
    }

    suspend fun saveUserFilterProfileSettings(
        art: String,
        userId: String,
        screenKey: String,
        profiles: Map<String, Map<String, String>>,
        selectedProfile: String?,
        saveUserSettingsFile: suspend (String, String, String, String) -> Unit
    ) {
        if (screenKey.isBlank() || userId.isBlank()) return
        filterProfileSettingsCache = filterProfileSettingsCache.filter { it.screenKey != screenKey } +
                UserCsvProfileSettings(
                    screenKey = screenKey,
                    profiles = profiles,
                    selectedProfile = selectedProfile.orEmpty()
                )
        val body = json.encodeToString(ListSerializer(UserCsvProfileSettings.serializer()), filterProfileSettingsCache)
        saveUserSettingsFile(art, userId, "filterprofilesettings.json", body)
    }

    suspend fun saveUserLetterProfileSettings(
        art: String,
        userId: String,
        screenKey: String,
        profiles: Map<String, String>,
        selectedProfile: String?,
        saveUserSettingsFile: suspend (String, String, String, String) -> Unit
    ) {
        if (screenKey.isBlank() || userId.isBlank()) return
        letterProfileSettingsCache = letterProfileSettingsCache.filter { it.screenKey != screenKey } +
                UserLetterProfileSettings(
                    screenKey = screenKey,
                    profiles = profiles,
                    selectedProfile = selectedProfile.orEmpty()
                )
        val body = json.encodeToString(ListSerializer(UserLetterProfileSettings.serializer()), letterProfileSettingsCache)
        saveUserSettingsFile(art, userId, "letterprofilesettings.json", body)
    }

    private suspend fun loadColumnSettings(
        art: String,
        userId: String,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val raw = loadUserSettingsFile(art, userId, "columnsettings.json")
        columnSettingsCache = if (raw.isBlank()) emptyList() else runCatching {
            json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)
        }.getOrDefault(emptyList())
    }

    private suspend fun loadRingSettings(
        art: String,
        userId: String,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val raw = UserSettingsApi.loadUserSmartRingSettings(userId).ifBlank {
            loadUserSettingsFile(art, userId, "ringsettings.json")
        }
        ringSettingsCache = parseRingSettings(raw)
        ringSettingsLoaded = true
    }

    private fun normalizeRingSlots(screenKeys: List<String>): List<All> =
        List(10) { index ->
            val screenKey = screenKeys.getOrNull(index).orEmpty().trim().takeIf { it != "__empty__" }.orEmpty()
            All(id = "slot_${index + 1}", screenKey = screenKey)
        }

    private fun parseRingSettings(raw: String): List<All> {
        if (raw.isBlank()) return normalizeRingSlots(emptyList())
        val slotSettings = runCatching {
            json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)
        }.getOrNull()
        if (slotSettings != null) return normalizeRingSlots(slotSettings.map { it.screenKey })

        val legacySettings = runCatching {
            json.decodeFromString(ListSerializer(String.serializer()), raw)
        }.getOrDefault(emptyList())
        return normalizeRingSlots(legacySettings)
    }

    private suspend fun loadCsvSettings(
        art: String,
        userId: String,
        isImport: Boolean,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val fileName = if (isImport) "csvimportsettings.json" else "csvexportsettings.json"
        val raw = loadUserSettingsFile(art, userId, fileName)
        val loaded = if (raw.isBlank()) emptyList() else runCatching {
            json.decodeFromString(ListSerializer(UserCsvProfileSettings.serializer()), raw)
        }.getOrDefault(emptyList())
        if (isImport) csvImportSettingsCache = loaded else csvExportSettingsCache = loaded
    }

    private suspend fun loadPopupSettings(
        art: String,
        userId: String,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val raw = loadUserSettingsFile(art, userId, "popupsettings.json")
        popupSettingsCache = if (raw.isBlank()) emptyMap() else runCatching {
            json.decodeFromString(MapSerializer(String.serializer(), ListSerializer(Double.serializer())), raw)
        }.getOrDefault(emptyMap())
    }

    private suspend fun loadFilterProfileSettings(
        art: String,
        userId: String,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val raw = loadUserSettingsFile(art, userId, "filterprofilesettings.json")
        filterProfileSettingsCache = if (raw.isBlank()) emptyList() else runCatching {
            json.decodeFromString(ListSerializer(UserCsvProfileSettings.serializer()), raw)
        }.getOrDefault(emptyList())
    }

    private suspend fun loadLetterProfileSettings(
        art: String,
        userId: String,
        loadUserSettingsFile: suspend (String, String, String) -> String
    ) {
        val raw = loadUserSettingsFile(art, userId, "letterprofilesettings.json")
        letterProfileSettingsCache = if (raw.isBlank()) emptyList() else runCatching {
            json.decodeFromString(ListSerializer(UserLetterProfileSettings.serializer()), raw)
        }.getOrDefault(emptyList())
    }
}
